import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Eye, EyeOff, Loader2, Lock, Mail, ArrowRight } from "lucide-react";

import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { useAuthStore, AuthUser } from "@/stores/auth.store";

const loginSchema = z.object({
  email: z.string().min(1, "Vui lòng nhập email hoặc tên đăng nhập / mã nhân viên"),
  password: z.string().min(1, "Vui lòng nhập mật khẩu"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const setAuth = useAuthStore((state) => state.setAuth);

  useEffect(() => {
    setMounted(true);
    const authError = sessionStorage.getItem("auth_error");
    if (authError) {
      setError(authError);
      sessionStorage.removeItem("auth_error");
    }
  }, []);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await trpcClient.auth.login.mutate({
        email: data.email,
        password: data.password,
      });
      const { user, token, refreshToken } = result;
      setAuth(user as AuthUser, { accessToken: token, refreshToken });

      window.location.href = "/";
    } catch (err: any) {
      const apiMessage = err?.message || err?.response?.data?.message;
      setError(
        apiMessage || "Không thể kết nối đến server. Vui lòng thử lại sau.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex w-full bg-emerald-950 lg:bg-white font-sans text-slate-900 relative overflow-hidden">
      {/* Mobile Background Elements (hidden on desktop) */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px] lg:hidden" />
      <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-emerald-600/30 blur-[100px] translate-x-1/3 -translate-y-1/3 lg:hidden" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-teal-600/20 blur-[100px] -translate-x-1/3 translate-y-1/3 lg:hidden" />

      {/* Left Panel - Branding (Hidden on mobile) */}
      <div className="hidden lg:flex w-1/2 flex-col justify-between relative bg-emerald-950 overflow-hidden text-emerald-50 p-12">
        {/* Background effects */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-emerald-700/20 blur-[100px] translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full bg-teal-600/20 blur-[120px] -translate-x-1/4 translate-y-1/4" />

        <div className="relative z-10 flex items-center gap-3">
          <img
            src="/artpresto-logo.png"
            alt="ARTPRESTO"
            className="h-14 object-contain drop-shadow-lg"
          />
        </div>

        <div className="relative z-10 mt-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-800/60 border border-emerald-600/40 backdrop-blur-sm mb-6 text-emerald-200 text-xs font-semibold tracking-wide">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
            </span>
            QUẢN TRỊ SẢN XUẤT BAO BÌ
          </div>
          <h1 className="text-4xl xl:text-5xl font-bold tracking-tight mb-4 leading-[1.15] text-white">
            APV Manager
            <br />
            <span className="text-emerald-300 font-light">
              Print & Packaging.
            </span>
          </h1>
          <p className="text-emerald-100/90 max-w-md text-base leading-relaxed">
            Hệ thống quản lý toàn diện quy trình sản xuất bao bì giấy Offset —
            từ báo giá, lệnh sản xuất, kế hoạch vật tư đến giao hàng và công nợ.
          </p>
        </div>

        <div className="relative z-10 mt-20 flex items-center gap-6 text-sm font-medium text-emerald-300/70">
          <p>© 2026 Artpresto Vietnam</p>
          <div className="h-1 w-1 rounded-full bg-emerald-400/40" />
          <p>Powered by EMS Solution</p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 lg:p-12 relative z-10 lg:bg-white">
        <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-slate-50 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 hidden lg:block" />

        <div
          className={`w-full max-w-[420px] transition-all duration-700 relative z-10 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
        >
          <div className="bg-white lg:bg-transparent rounded-[2rem] lg:rounded-none shadow-2xl lg:shadow-none p-6 sm:p-10 lg:p-0">
            {/* Mobile Header (Hidden on large screens where left panel is visible) */}
            <div className="lg:hidden flex flex-col items-center text-center mb-8">
              <div className="h-16 w-16 bg-slate-50/80 rounded-2xl flex items-center justify-center mb-4 border border-slate-100 shadow-sm backdrop-blur-sm">
                <img
                  src="/artpresto-logo.png"
                  alt="ARTPRESTO"
                  className="h-10 object-contain"
                />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">APV Manager</h1>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mt-1">
                Quản trị sản xuất bao bì
              </p>
            </div>

            <div className="mb-8 text-center lg:text-left">
              <h2 className="text-xl lg:text-3xl font-bold text-slate-900 mb-2">
                Đăng nhập tài khoản
              </h2>
              <p className="text-slate-500 text-sm">
                Vui lòng nhập thông tin xác thực để tiếp tục.
              </p>
            </div>

            {error && (
              <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm flex items-start gap-2 animate-in fade-in slide-in-from-top-2">
                <span className="shrink-0 mt-0.5">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 lg:space-y-5"
            >
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-slate-700">
                  Email hoặc Tên đăng nhập / Mã NV
                </label>
                <div className="relative group">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                  <input
                    type="text"
                    placeholder="admin hoặc name@artpresto.com.vn"
                    className="w-full h-12 pl-11 pr-4 rounded-xl bg-slate-50 lg:bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-sm shadow-sm"
                    {...form.register("email")}
                    autoComplete="username"
                  />
                </div>
                {form.formState.errors.email && (
                  <p className="text-xs text-red-500 font-medium">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-slate-700">
                    Mật khẩu
                  </label>
                  <a
                    href="#"
                    className="text-xs font-semibold text-emerald-600 hover:text-emerald-500 transition-colors"
                  >
                    Quên mật khẩu?
                  </a>
                </div>
                <div className="relative group">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="w-full h-12 pl-11 pr-12 rounded-xl bg-slate-50 lg:bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-sm shadow-sm"
                    {...form.register("password")}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {form.formState.errors.password && (
                  <p className="text-xs text-red-500 font-medium">
                    {form.formState.errors.password.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="w-full h-12 mt-6 lg:mt-8 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm rounded-xl shadow-lg shadow-slate-900/10 hover:shadow-slate-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 group"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Đang xác thực...</span>
                  </>
                ) : (
                  <>
                    <span>Đăng nhập hệ thống</span>
                    <ArrowRight className="h-4 w-4 transform group-hover:translate-x-1.5 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </div>

          <div className="lg:hidden mt-8 text-center">
            <p className="text-xs font-medium text-emerald-200/60 drop-shadow-sm">
              © 2026 Artpresto Vietnam
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
